import { request } from "../../../Services/Api"
import { normalizeDetail, normalizeListResponse, toQuery } from "./normalize"

// Every Recover call goes to the dispute_management Django app. `request.api` already
// prefixes /api/, so the full path is /api/dispute-management/... The demo server used
// /recover/* and those paths were never ours; see dispute_management/urls.py for the
// authoritative list.
const BASE = "/dispute-management"

// Reason-category taxonomy. Shared by Preferences (per-category rules) and AllPage
// (filter dropdown). Source of truth is the backend.
export const getReasonCategories = () => request.api.get(`${BASE}/reason-categories/`)

// Gating payload the shell reads to pick the activated / deactivated / empty experience:
// { activated, ever_activated, is_demo, processors: { connected }, disputes_count }.
// The profile carries every connected processor's answers at once; `processor` only picks
// which one the progress figures in the response describe.
export const getRecoverProfile = (processor) =>
  request.api.get(`${BASE}/profile/`, processor ? { processor } : {})

// Activation, per connected processor. Naming none means every connected one, which is
// what a merchant with a single connection is asking for.
export const activateRecover = (processor) =>
  request.api.post(`${BASE}/activate/`, processor ? { processor } : {})
export const deactivateRecover = (processor) =>
  request.api.post(`${BASE}/deactivate/`, processor ? { processor } : {})

// Pull the merchant's disputes from their connected processors on demand instead of
// waiting for the hourly beat. Refused while Recover is deactivated.
export const syncRecoverDisputes = () => request.api.post(`${BASE}/sync/`, {})

export const getDisputes = (params) =>
  request.api.get(`${BASE}/disputes/`, toQuery(params)).then(normalizeListResponse)
export const getDisputesStatistics = (params) =>
  request.api.get(`${BASE}/disputes/statistics/`, params)

// Detail. Opening a dispute is what triggers the first evidence version server-side, so
// the response carries an evidence state block alongside the row.
export const getDispute = (id) =>
  request.api
    .get(`${BASE}/disputes/${id}/`)
    .then((response) =>
      response?.ok && response?.data
        ? { ...response, data: normalizeDetail(response.data) }
        : response
    )
export const getEvidence = (id) => request.api.get(`${BASE}/disputes/${id}/evidence/`)

// Cardholder support history pulled from the connected enrichment tool (Intercom today).
export const getSupportContext = (id) => request.api.get(`${BASE}/disputes/${id}/support-context/`)

// Invited team members for the Assignee feature (same endpoint the Settings team page
// reads, so it is not under the Recover prefix).
export const getTeamMembers = () => request.api.get("/user/user-management/available_memberships/")

// Integrate step: which processors can serve Recover, and asking for one we do not have.
export const getIntegrationCatalog = () => request.api.get(`${BASE}/integrations/`)
export const requestIntegration = (body) => request.api.post(`${BASE}/integrations/requests/`, body)

// Does the connected shop still owe us the Recover scopes? Returns
// { connected, needs_upgrade, missing_scopes, reauth_url }. `reauth_url` upgrades the
// existing installation in place — the merchant confirms a new grant, nothing reinstalls.
// This is what tells a merchant who connected Shopify long ago for Alerts that Recover
// needs a reconnect.
export const getShopifyScopeStatus = () => request.api.get(`${BASE}/shopify/scope-status/`)

// Enrichment tools. Connecting is an OAuth redirect: we ask the backend for the authorize
// URL and send the browser there; the callback lands back on the backend.
export const getEnrichmentTools = () => request.api.get(`${BASE}/enrichment/tools/`)
// `returnUrl` is the in-app path the backend sends the merchant back to, success or failure.
export const getEnrichmentConnectUrl = (provider, returnUrl) =>
  request.api.get(
    `${BASE}/enrichment/${provider}/connect-url/`,
    returnUrl ? { return_url: returnUrl } : {}
  )
export const disconnectEnrichmentTool = (provider) =>
  request.api.post(`${BASE}/enrichment/${provider}/disconnect/`, {})

// Configure answers, stored per connected processor: the same question can hold a different
// answer for Stripe and for Shopify, so every save says which one it is for. The response
// carries the server's own answered/total count, which is what the rail renders.
export const patchRecoverProfileSection = ({ section, patch, processor }) =>
  request.api.patch(
    `${BASE}/profile/${section}/${processor ? `?processor=${encodeURIComponent(processor)}` : ""}`,
    patch
  )

// Records the profile as set up for one processor. Deliberately NOT activation — Recover only
// goes live when the merchant activates it on Preview & Confirm.
export const finishRecoverSetup = (processor) =>
  request.api.post(
    `${BASE}/profile/finish/${processor ? `?processor=${encodeURIComponent(processor)}` : ""}`,
    {}
  )

// --------------------------------------------------------------------------------------
// Not built on the backend yet. Paths below are the shape we expect, not routes that
// answer today — every one of them needs its own backend ticket before the feature that
// calls it can work. Tracked from CHA1-2288; see its endpoint inventory.
// --------------------------------------------------------------------------------------

export const patchRecoverPreferences = (patch) =>
  request.api.patch(`${BASE}/profile/preferences/`, patch)

const uploadSlotUrl = ({ section, slot, processor }) =>
  `${BASE}/profile/upload/${slot}/?${new URLSearchParams({
    section: section || "evidence_vault",
    ...(processor ? { processor } : {})
  })}`
export const uploadVisualEvidence = ({ section, slot, processor, formData }) =>
  request.api.post(uploadSlotUrl({ section, slot, processor }), formData)

export const removeVisualEvidence = ({ section, slot, processor }) =>
  request.api.delete(uploadSlotUrl({ section, slot, processor }))
export const prefillFromWebsite = (body) =>
  request.api.post(`${BASE}/profile/prefill_from_website/`, body, { timeout: 25000 })

// Row actions.
export const pinDispute = (id, pin) => request.api.post(`${BASE}/disputes/${id}/pin/`, { pin })
export const assignDispute = (id, memberId) =>
  request.api.post(`${BASE}/disputes/${id}/assign/`, { assigned_to_id: memberId })
export const reopenDispute = (id) => request.api.post(`${BASE}/disputes/${id}/reopen/`)
export const getDisputesExport = (params) =>
  request.api.get(`${BASE}/disputes/export/`, toQuery(params), { responseType: "blob" })

// Evidence review. Generation itself is implicit on detail load; an explicit rebuild and
// the approve/reject pair are still to come.
export const generateEvidence = (id, body) =>
  request.api.post(`${BASE}/disputes/${id}/generate_evidence/`, body || {})
export const approveEvidence = (id, version, body) =>
  request.api.post(`${BASE}/disputes/${id}/evidence/${version}/approve/`, body || {})
export const rejectEvidence = (id, version, body) =>
  request.api.post(`${BASE}/disputes/${id}/evidence/${version}/reject/`, body || {})

// Configure preview. Renders the evidence to a watermarked preview server-side and returns
// { page_count }; the modal then paints each page from the page endpoint onto a canvas, so
// the clean PDF never reaches the browser. Lands with CHA1-1451.
export const generatePreview = (body) => request.api.post(`${BASE}/preview/`, body)

// One rendered page of an evidence document, as a blob. The caller decodes it straight to
// an ImageBitmap and paints it onto a canvas — the submittable PDF has no route of its own
// and never reaches the browser.
export const getEvidencePage = (disputeId, version, page) =>
  request.api.get(
    `${BASE}/disputes/${disputeId}/evidence/${version}/page/${page}.png`,
    {},
    { responseType: "blob" }
  )

// The Configure preview's pages. Both this and `generatePreview` above address a
// `preview/` endpoint the backend does not have yet, so the modal stays inert until it
// does; the paths are written the way every other Recover call is so there is one
// convention to follow when it lands.
export const getPreviewPage = (page) =>
  request.api.get(`${BASE}/preview/page/${page}.png`, {}, { responseType: "blob" })
